import React, { createContext, useState, useContext, useMemo, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';

// Create a context for theme settings
const ThemeContext = createContext();

// Custom hook to use the theme context
export const useTheme = () => useContext(ThemeContext);

// Theme provider component
export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or system preference or default to 'light'
  const [mode, setMode] = useState(() => {
    try {
      // First, try to get saved theme from localStorage
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
        return savedTheme;
      }
      
      // If no saved theme, check system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        return 'dark';
      }
      
      // Default to light mode
      return 'light';
    } catch (error) {
      console.warn('Failed to load theme from localStorage:', error);
      return 'light';
    }
  });
  
  // Save theme to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('theme', mode);
    } catch (error) {
      console.warn('Failed to save theme to localStorage:', error);
    }
  }, [mode]);

  // Listen for system theme changes (optional enhancement)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e) => {
      // Only change if user hasn't set a preference (no theme in localStorage)
      const savedTheme = localStorage.getItem('theme');
      if (!savedTheme) {
        setMode(e.matches ? 'dark' : 'light');
      }
    };

    // Add listener for system theme changes
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleSystemThemeChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleSystemThemeChange);
    }

    // Cleanup listener on unmount
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleSystemThemeChange);
      } else {
        // Fallback for older browsers
        mediaQuery.removeListener(handleSystemThemeChange);
      }
    };
  }, []);
  
  // Create theme based on current mode
  const theme = useMemo(() => createTheme({
    palette: {
      mode,
      primary: {
        main: mode === 'light' ? '#0077b6' : '#90caf9',
        light: mode === 'light' ? '#e0f2f7' : '#4b9fea',
        dark: mode === 'light' ? '#00509d' : '#0d47a1',
        contrastText: '#fff',
      },
      secondary: {
        main: mode === 'light' ? '#f50057' : '#f48fb1',
        light: mode === 'light' ? '#ff4081' : '#f6a5c0',
        dark: mode === 'light' ? '#c51162' : '#bf5f82',
        contrastText: mode === 'light' ? '#fff' : '#000',
      },
      background: {
        default: mode === 'light' ? '#f5f5f5' : '#121212',
        paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
      },
      text: {
        primary: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : 'rgba(255, 255, 255, 0.87)',
        secondary: mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)',
      },
      action: {
        active: mode === 'light' ? 'rgba(0, 0, 0, 0.54)' : 'rgba(255, 255, 255, 0.7)',
        hover: mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h4: {
        fontWeight: 600,
      },
      h5: {
        fontWeight: 500,
      },
      h6: {
        fontWeight: 500,
      },
    },
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: mode === 'light' 
              ? '0px 2px 4px -1px rgba(0,0,0,0.1), 0px 4px 5px 0px rgba(0,0,0,0.07), 0px 1px 10px 0px rgba(0,0,0,0.06)'
              : '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            textTransform: 'none',
            fontWeight: 500,
          },
          outlined: {
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(144, 202, 249, 0.08)' : undefined,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 600,
            backgroundColor: mode === 'light' ? '#f5f5f5' : '#2d2d2d',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            '& .MuiChip-label': {
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : undefined,
            },
          },
          colorSecondary: {
            backgroundColor: mode === 'dark' ? '#ad1457' : undefined,
            '& .MuiChip-label': {
              color: mode === 'dark' ? '#fff' : undefined,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: mode === 'dark' ? 'rgba(255, 255, 255, 0.7)' : undefined,
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : undefined,
            },
          },
        },
      },
    },
  }), [mode]);
  
  // Toggle between light and dark mode
  const toggleTheme = () => {
    setMode(prevMode => prevMode === 'light' ? 'dark' : 'light');
  };

  // Set theme directly (useful for programmatic theme changes)
  const setTheme = (newMode) => {
    if (newMode === 'light' || newMode === 'dark') {
      setMode(newMode);
    }
  };

  // Reset theme to system preference
  const resetToSystemTheme = () => {
    try {
      // Remove saved theme from localStorage
      localStorage.removeItem('theme');
      
      // Set theme based on system preference
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        setMode('dark');
      } else {
        setMode('light');
      }
    } catch (error) {
      console.warn('Failed to reset theme to system preference:', error);
    }
  };
  
  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setTheme, resetToSystemTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline /> {/* Adds baseline styles and normalizes CSS */}
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};